import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import Draggable from 'vuedraggable';
import { currencies, POST_LOADER_DELAY } from '@/constants';
import { IAsset, ICategory } from '@/services/asset';
import checks, { LinesheetChecks } from '@/services/checks';
import Accordion from '@/components/Accordion.vue';
import AdBanner from '@/components/AdBanner.vue';
import AssetManagerRegion from '@/components/asset-manager/AssetManagerRegion.vue';
import CheckList from '@/components/CheckList.vue';
import ChromeEditor from '@/components/chrome/Editor.vue';
import ChromeEditorPanel from '@/components/chrome/EditorPanel.vue';
import Dropdown from '@/components/Dropdown.vue';
import FieldBase from '@/components/field/Base.vue';
import ListBody from '@/components/list/ListBody.vue';
import ListItem from '@/components/list/ListItem.vue';
import LoaderInline from '@/components/LoaderInline.vue';
import PillContent from '@/components/pill/PillContent.vue';
import ShareBox from '@/components/ShareBox.vue';
import ShowGuide from '@/components/ShowGuide.vue';
import settings from '../../settings';
import { methods, messages } from '../../common';
import { Linesheet, Product, UserProduct } from '../../types';
import ProductEditor from './ProductEditor.vue';
import ProductView from './ProductView.vue';
import ViewportHeader from './ViewportHeader.vue';
import checklistActions from './checks';

const formattedCurrencies = Object
  .keys(currencies)
  .reduce((p, key) => {
    const data = (currencies as any)[key];
    p[key] = `${data.name} (${data.symbol_native})`;
    return p;
  }, {} as any);

export default (storeName: string) => ({
  mounted() {
    this.context = this;
    this.$route.params.linesheetId === 'create'
      ? methods.createLinesheet(this)
      : this.fetchData();
  },
  data() {
    const self = this;
    return {
      assetValidation: {
        fileLimit: 1,
      },
      checkListActionss: checklistActions(this),
      currencyParams: {
        title: 'Choose a currency',
        type: 'option',
        values: formattedCurrencies,
      },
      fetched: false,
      loadingProductLibrary: false,
      guide: [
        {
          point: '[class*="ViewportHeader_header"]',
        },
        {
          ref: 'basicInfo',
          onEnter: () => {
            self.selectPanel('info');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          point: '#currencyInfo',
          dialog: {
            selector: '#basicInfo',
            content: 'Add basic linesheet and contact information here. Don\'t forget to include important notes such as delivery times, shipment periods, minimum orders etc.',
          },
        },
        {
          ref: 'currency',
          point: '#sharingInfo',
          dialog: {
            selector: '#currencyInfo',
            content: 'Add the currencies that apply to both your products and your buyers. This is important especially for international buyers so they don\'t have to convert all the prices.',
          },
        },
        {
          ref: 'sharing',
          point: '[class*="SidebarWrapper_toolbar"] [title="Products"]',
          dialog: {
            selector: '#sharingInfo',
            content: 'Ready to share this with a buyer or someone to proofread? Copy the url below and send it to them.',
          },
        },
        {
          ref: 'products',
          onEnter: () => {
            self.selectPanel('products');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          point: '[class*="SidebarWrapper_toolbar"] [title="Checks"]',
          dialog: {
            selector: '[class*="SidebarWrapper_panelsOuter"]',
            content: 'Product information is key. Import existing products or create new products with images, wholesale/retail prices and key properties such as SKUs, composition, origin etc. Tip: You can reorder the products by dragging them',
          },
        },
        {
          onEnter: () => {
            self.selectPanel('checks');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          dialog: {
            selector: '[class*="SidebarWrapper_panelsOuter"]',
            content: 'Try and complete all the tasks listed. The more relevant information you provide, the more confidence your buyer will have.',
          },
        },
      ],
      settings,
    };
  },
  computed: {
    ...mapState('chromeEditor', [
      'panel',
    ]),
    ...mapState(storeName, [
      'productLibrary',
      'view',
    ]),
    contact(): object {
      const { fields } = settings.contact.params;
      const contact = this.data.contact || {};
      return Object
          .keys(fields)
          .reduce((a: any, field: string) => {
            a[field] = contact[field];
            return a;
          }, {});
    },
    data: {
      get(): Linesheet {
        return this.$store.state.linesheet.data;
      },
      set(data: Linesheet) {
        this.$store.commit(`${storeName}/LOAD`, data);
      },
    },
    linesheetId(): string {
      return this.$route.params.linesheetId;
    },
    listOfChecks() {
      return checks(LinesheetChecks, this.data);
    },
    products: {
      get(): Product[] {
        return this.data.products;
      },
      set(products: Product[]) {
        this.$store.dispatch(`${storeName}/productsUpdate`, products);
      },
    },
    shareUrl(): string {
      return window.location.href;
    },
  },
  watch: {
    $route: 'fetchData',
  },
  methods: {
    ...mapActions('chromeEditor', [
      'selectPanel',
    ]),
    ...mapActions(storeName, [
      'archive',
      'create',
      'load',
      'loadProducts',
      'productAdd',
      'productsClear',
      'productClone',
      'productExport',
      'productRemove',
      'productSelect',
    ]),
    archiveToggle(provided?: boolean) {
      const value = provided !== undefined ? provided : !this.data.archived;
      const title = value ? 'Archiving' : 'Unarchiving';
      this.archive(!this.data.archived);
      this.$store.commit('loader/ADD', {
        title,
        promise: this.save(this.linesheetId),
      });
    },
    askDelete() {
      methods
        .askDelete
        .bind(this)(this.data)
        .then((deleted: boolean) => (
          deleted && this.$router.push({ name: 'linesheets' })
        ));
    },
    assetRecord: (product: Product): any => ({
      type: ICategory.LinesheetProduct,
      type_id: product.id,
    }),
    fetchData() {
      this.fetched = false;
      const linesheetId = this.linesheetId;
      const promise = this
        .load(linesheetId)
        .then((linesheet: Linesheet) => {
          // flatten linesheet product assets into single array
          // and sync with asset composer
          return this.assetComposerSyncAssets(
            linesheet.products.reduce((all, product) => (
              all.concat(...(product.assets || []).map(asset => ({
                ...asset,

                /*
                  To enable asset to be filtered in product editor
                  asset manager items need a type_id
                  which is different based on the asset type.
                  In this case type_id = linesheet_product_id
                */
                type_id: (asset as any).linesheet_product_id,
              })))
            ), [] as IAsset[]),
          );
        })
        .then(
          () => this.fetched = true,
          (error: any) => methods.loadError(this, error),
        );

      this.$store.commit('loader/ADD', {
        title: 'Fetching data',
        promise,
      });
    },
    goToLinesheets() {
      this.$router.push({
        name: 'linesheets',
      });
    },
    print() {
      window.print();
    },
    productAddView(payload?: Product) {
      this.productAdd(payload);
      this.selectPanel('products');
      this.productSelect(this.$store.getters[`${storeName}/productCount`] - 1);
    },
    async productImport() {
      const self = this;

      // don't try and load if already loading
      if (this.loadingProductLibrary) {
        return;
      }

      this.loadingProductLibrary = true;
      let products;
      try {
        // load products into dialog format
        products = await this.loadProducts();
      } catch (e) {
        this.loadingProductLibrary = false;
        return;
      }

      this.loadingProductLibrary = false;
      const options = (products as UserProduct[]).reduce((allProducts, product) => {
        // don't include products already added to library
        const linesheetProducts: Product[] = this.data.products;
        const isListed = linesheetProducts.find(item => (
          item.product_id === product.id
        ));
        if (!isListed) {
          allProducts[product.id] = product.name;
        }
        return allProducts;
      }, {} as {
        [id: string]: string;
      });

      // create option dialog
      this.$store.commit('dialog/CREATE', {
        title: 'Import products',
        component: Vue.extend({
          data: () => ({
            value: [],
            options,
          }),
          methods: {
            change(productIds: string[]) {
              productIds.forEach(productId => {
                const product = (self.productLibrary as UserProduct[])
                  .find(item => item.id === productId);

                // add product in shape of imported product
                product
                  && self.productAddView({
                    ...product,
                    product_id: productId,
                    assets: undefined,
                    prices: undefined,
                    product,
                  });
              });
            },
          },
          components: {
            Dropdown,
          },
          template: `
            <dropdown
              v-model="value"
              :is-option="true"
              :options="options"
              @close="$emit('close')"
              @change="change"
            />
          `,
        }),
      });
    },
    productSelectView(index: number) {
      this.productSelect(index);
      this.selectPanel('products');
    },
    save() {
      return this
        .$store
        .dispatch(`${storeName}/save`)
        .catch((error: any) => {
          console.error(error);

          this.$store.commit('dialog/CREATE', {
            title: 'Save error',
            content: messages.ERROR_LINESHEET_SAVE,
            compact: true,
            delay: POST_LOADER_DELAY,
            buttons: [{
              text: 'Close',
              click: (message: any) => message.close(),
            }],
          });
        });
    },
    toggleProduct(index: number) {
      const idx = index === this.view.selectedProduct ? -1 : index;
      this.productSelect(idx);
    },
    update() {
      this.$store.commit('loader/ADD', {
        title: 'Saving',
        promise: this.save(this.linesheetId),
      });
    },
  },
  components: {
    Accordion,
    AdBanner,
    AssetManagerRegion,
    CheckList,
    ChromeEditor,
    ChromeEditorPanel,
    Draggable,
    FieldBase,
    ListBody,
    ListItem,
    LoaderInline,
    PillContent,
    ProductEditor,
    ProductView,
    ShareBox,
    ShowGuide,
    ViewportHeader,
  },
} as IView);
